import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, ButtonGroup } from 'semantic-ui-react';
import { LoadingWrapper } from './LoadingWrapper';
import { getBlobReadUrl, getBlobDownloadUrl } from '../../services/azureBlobService';

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2];

/**
 * Generic modal video player backed by Azure Blob Storage.
 *
 * Fetches a short-lived read SAS URL via GET /blob/read-url/?blobName=...
 * and feeds it directly into a <video> element.
 *
 * @param {string}   blobName - Full blob path (e.g. 'videos/incoming/clip_<uuid>.mp4')
 * @param {string}   [title]  - Optional title shown in the modal header
 * @param {function} onClose  - Called when the modal is dismissed
 */
export function BlobVideoPlayerModal({ blobName, title, onClose }) {
    const [sasUrl, setSasUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [speed, setSpeed] = useState(1);
    const [downloading, setDownloading] = useState(false);
    const videoRef = useRef(null);

    const handleSpeedChange = (rate) => {
        setSpeed(rate);
        if (videoRef.current) videoRef.current.playbackRate = rate;
    };

    const handleDownload = async () => {
        if (!blobName) return;
        setDownloading(true);
        try {
            const { sasUrl: downloadUrl } = await getBlobDownloadUrl(blobName);
            const anchor = document.createElement('a');
            anchor.href = downloadUrl;
            anchor.target = '_blank';
            anchor.rel = 'noopener noreferrer';
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
        } catch (err) {
            console.error('BlobVideoPlayerModal: download failed:', err);
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        setSasUrl(null);

        getBlobReadUrl(blobName)
            .then(({ sasUrl: url }) => {
                if (!cancelled) setSasUrl(url);
            })
            .catch((err) => {
                if (!cancelled) setError('Failed to load video. Please try again.');
                console.error('BlobVideoPlayerModal: failed to fetch read URL:', err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [blobName]);

    return (
        <Modal open onClose={onClose} size="large" closeIcon>
            <Modal.Header>{title || 'Video'}</Modal.Header>
            <Modal.Content>
                <LoadingWrapper loading={loading} text="Loading video">
                    {error && <p style={{ color: 'red' }}>{error}</p>}
                    {sasUrl && (
                        <>
                            <video
                                ref={videoRef}
                                controls
                                aria-label={title || 'Video'}
                                style={{ width: '100%', maxHeight: '70vh' }}
                                src={sasUrl}
                            >
                                Your browser does not support the video tag.
                            </video>
                            <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '0.85em', color: '#555' }}>Speed:</span>
                                <ButtonGroup size="mini">
                                    {SPEED_OPTIONS.map((rate) => (
                                        <Button
                                            key={rate}
                                            primary={speed === rate}
                                            basic={speed !== rate}
                                            onClick={() => handleSpeedChange(rate)}
                                        >
                                            {rate}x
                                        </Button>
                                    ))}
                                </ButtonGroup>
                                <Button
                                    size="mini"
                                    icon="download"
                                    content="Download"
                                    loading={downloading}
                                    disabled={downloading}
                                    onClick={handleDownload}
                                />
                            </div>
                        </>
                    )}
                </LoadingWrapper>
            </Modal.Content>
        </Modal>
    );
}
